// Demo-mode persistence layer. Outside Telegram we have no auth and the
// worker's /api/* routes 401, so we route save/saved/me/profile through
// localStorage instead. The shape mirrors the worker's DTOs exactly so the
// rest of the app never has to branch on demo vs prod.
//
// Storage keys are namespaced under `saaslots:demo:*` so they coexist with
// /lib/rejected.ts and /lib/sound.ts state.

import type { IdeaDTO, Lang, ProfileDTO } from "./api";
import { readJson, writeJson } from "./storage";

const KEY_SAVED = "saaslots:demo:saved_v1";
const KEY_PROFILE = "saaslots:demo:profile_v1";

/* ── saved ideas ────────────────────────────────────────────────────── */

export function getSaved(): IdeaDTO[] {
  return readJson<IdeaDTO[]>(KEY_SAVED, []).filter(
    (x): x is IdeaDTO => !!x && typeof x === "object" && typeof x.id === "number",
  );
}

export function addSaved(idea: IdeaDTO): void {
  const now = Math.floor(Date.now() / 1000);
  const cur = getSaved().filter((i) => i.id !== idea.id);
  cur.unshift({ ...idea, saved: true, saved_at: now });
  writeJson(KEY_SAVED, cur);
  // Single profile read+write inside bumpProfile.
  bumpProfile({ saved_count: 1 });
}

export function removeSaved(id: number): void {
  const cur = getSaved();
  const next = cur.filter((i) => i.id !== id);
  if (next.length === cur.length) return;
  writeJson(KEY_SAVED, next);
  bumpProfile({ saved_count: -1 });
}

/* ── profile ────────────────────────────────────────────────────────── */

function defaultProfile(): ProfileDTO {
  const browserLang =
    typeof navigator !== "undefined" && navigator.language?.startsWith("ru")
      ? ("ru" as Lang)
      : ("en" as Lang);
  return {
    telegram_id: 0,
    username: null,
    first_name: null,
    language: browserLang,
    about_text: "",
    total_spins: 0,
    saved_count: 0,
    // Demo mode: hide the paid economy entirely. 0/0 keeps the pill
    // hidden and the buy modal unreachable, demo is unlimited spins.
    free_left: 0,
    free_limit: 0,
    credits: 0,
  };
}

export function getProfile(): ProfileDTO {
  const stored = readJson<Partial<ProfileDTO> | null>(KEY_PROFILE, null);
  return stored ? { ...defaultProfile(), ...stored } : defaultProfile();
}

export function updateProfile(patch: Partial<ProfileDTO>): ProfileDTO {
  const next: ProfileDTO = { ...getProfile(), ...patch };
  writeJson(KEY_PROFILE, next);
  return next;
}

/**
 * Mutate counters by deltas (positive or negative) and return the updated
 * profile. Single read + single write per call, earlier versions did up
 * to three parses per spin because each helper rebroadcast through the
 * other.
 */
function bumpProfile(delta: { total_spins?: number; saved_count?: number }): ProfileDTO {
  const cur = getProfile();
  const next: ProfileDTO = {
    ...cur,
    total_spins: Math.max(0, cur.total_spins + (delta.total_spins ?? 0)),
    saved_count: Math.max(0, cur.saved_count + (delta.saved_count ?? 0)),
  };
  writeJson(KEY_PROFILE, next);
  return next;
}

export function bumpSpinCounter(): ProfileDTO {
  return bumpProfile({ total_spins: 1 });
}
